import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router";

import { MapPanel, MapPanelScrollableArea } from "~/modules/map/components/MapPanel";

import OverrideAutoValidationSection from "../../components/OverrideAutoValidationSection";
import { ProjectReviewVisitV2RejectButton } from "../../components/ProjectReviewVisitV2RejectButton";
import { useReviewVisitDecisionActions } from "../../hooks/useReviewVisitDecisionActions";
import { useProjectReviewVisitV2TimeReportsRoute_GetData } from "./api/useProjectReviewVisitV2TimeReportsRoute_GetData";
import { useProjectReviewVisitV2TimeReportsRoute_OverrideAutoValidation } from "./api/useProjectReviewVisitV2TimeReportsRoute_OverrideAutoValidation";
import { useProjectReviewVisitV2TimeReportsRoute_UpdateStartEndTimes } from "./api/useProjectReviewVisitV2TimeReportsRoute_UpdateStartEndTimes";
import TimeReportsEmptyState from "./components/TimeReportsEmptyState";
import { TimeReportsHeader } from "./components/TimeReportsHeader";
import TimeReportsList from "./components/TimeReportsList";
import { TimeReportsPanelSkeleton } from "./components/TimeReportsPanelSkeleton";
import { TimeReportsVisitSummary } from "./components/TimeReportsVisitSummary";
import { ProjectReviewVisitV2TimeReportsRoute_GetDataDocument } from "./queries.generated";

export function ProjectReviewVisitV2TimeReportsRoute() {
    const navigate = useNavigate();
    const { visitId } = useParams();
    if (!visitId) throw new Error("invalid params");

    const { visit, hasAllTimeRangesValid, isOverridden } = useProjectReviewVisitV2TimeReportsRoute_GetData(visitId);
    const overrideAutovalidation = useProjectReviewVisitV2TimeReportsRoute_OverrideAutoValidation(visitId);
    const { updateStartEndTimes } = useProjectReviewVisitV2TimeReportsRoute_UpdateStartEndTimes();

    const actions = useReviewVisitDecisionActions({
        visitId,
        refetchQueries: [ProjectReviewVisitV2TimeReportsRoute_GetDataDocument],
    });

    const handleOverrideClick = async (reason: string | null) => {
        await overrideAutovalidation.handleOverrideTimeAutoValidation(reason);
        if (reason !== null) navigate(-1);
    };

    const handleRejectClick = async (reason: string) => {
        await actions.handleReject(reason);
        navigate(-1);
    };

    if (visit === null || visit === undefined) {
        return <TimeReportsPanelSkeleton backHref="../" />;
    }

    return (
        <MapPanel>
            <TimeReportsHeader backHref="../" />

            <MapPanelScrollableArea>
                <Box
                    sx={theme => ({
                        display: "grid",
                        gridTemplateColumns: "2fr 1.5fr 1.5fr 1fr auto",
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    })}
                >
                    <TimeReportsVisitSummary visit={visit} />
                    {visit.reportsV2.length === 0 ? (
                        <TimeReportsEmptyState />
                    ) : (
                        <TimeReportsList reports={visit.reportsV2} visitStatus={visit.status} onUpdate={updateStartEndTimes} />
                    )}
                </Box>
                {visit.reportsV2.length > 0 && !hasAllTimeRangesValid && (
                    <OverrideAutoValidationSection
                        isOverridden={isOverridden}
                        overriddenReason={visit.timeProtocolAutoValidationOverriddenReason}
                        onOverride={reason => handleOverrideClick(reason)}
                        onRevert={() => handleOverrideClick(null)}
                        loading={overrideAutovalidation.loading}
                        error={overrideAutovalidation.error}
                    />
                )}

                <ProjectReviewVisitV2RejectButton visit={visit} onReject={handleRejectClick} loading={actions.loading} />
            </MapPanelScrollableArea>
        </MapPanel>
    );
}
